import { useState } from 'react';
import {
    Paper,
    Typography,
    TextField,
    Button,
    Container,
    Alert,
    Snackbar,
    Stack
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { sendEOTAnonymousMessage } from '../services/email';

const EOTAnonymousMessagePage = () => {
    const [message, setMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        const trimmedMessage = message.trim();
        if (!trimmedMessage) {
            setSnackbarMessage('Please enter a message before submitting.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setIsSubmitting(true);

        try {
            const success = await sendEOTAnonymousMessage({
                message: trimmedMessage
            });

            if (success) {
                setSnackbarMessage('Your anonymous message has been sent to eot@iodparc.com.');
                setSnackbarSeverity('success');
                setMessage('');
            } else {
                setSnackbarMessage('Unable to send your message right now. Please try again.');
                setSnackbarSeverity('error');
            }

            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error submitting EOT anonymous message:', error);
            setSnackbarMessage('An error occurred while sending your message.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#009688', mb: 3 }}>
                    Anonymous Message to EOT
                </Typography>

                <Stack spacing={3}>
                    <Alert severity="info">
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                            Thank you for wanting to reach out to the EOT. This form will allow you to send an anonymous
                            message to eot@iodparc.com, which is a dedicated email inbox to which only the current Employee
                            Directors have access. As the message will be anonymous, it will be impossible for the EOT to
                            respond you directly, or to provide clear feedback. However, please trust us in saying that we do
                            take anything that is brought to our attention very seriously, and where possible we will act on this
                            or signpost it to others where appropriate.
                        </Typography>
                    </Alert>

                    <TextField
                        fullWidth
                        required
                        multiline
                        rows={10}
                        label="Your anonymous message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Type your message here..."
                    />

                    <Stack direction="row" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{
                                bgcolor: '#009688',
                                '&:hover': {
                                    bgcolor: '#00796b'
                                }
                            }}
                        >
                            {isSubmitting ? 'Sending...' : 'Send anonymously'}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default EOTAnonymousMessagePage;
